import React from "react";
import { Table, InputNumber, Space, Button, Empty, Select } from "antd";
import { CloseOutlined, CalculatorOutlined } from "@ant-design/icons";
import { RecipeIngredient, RawMaterial } from "../../../Types";

interface Props {
  ingredients: RecipeIngredient[];
  allRawMaterials: RawMaterial[];
  onUpdate: (index: number, updates: Partial<RecipeIngredient>) => void;
  onRemove: (index: number) => void;
}

export const IngredientsEditor: React.FC<Props> = ({
  ingredients,
  allRawMaterials,
  onUpdate,
  onRemove,
}) => {
  if (ingredients.length === 0) {
    return (
      <div className="py-10 border border-dashed border-gray-200 rounded-md flex flex-col items-center justify-center bg-gray-50">
        <CalculatorOutlined
          style={{ fontSize: "24px" }}
          className="text-gray-300 mb-2"
        />
        <span className="text-xs text-gray-400">No ingredients added yet.</span>
      </div>
    );
  }

  const columns = [
    {
      title: "Raw Material",
      key: "raw_material",
      render: (_: any, rec: RecipeIngredient) => (
        <div>
          <span className="font-medium text-gray-800 text-xs">
            {rec.raw_material_name}
          </span>
          <div className="text-[11px] text-gray-400">
            {rec.raw_material_code}
          </div>
        </div>
      ),
    },
    {
      title: "Batch / Stock",
      key: "batch_stock",
      render: (_: any, rec: RecipeIngredient, idx: number) => {
        const mat = allRawMaterials.find((rm) => rm.id === rec.raw_material_id);
        const stocks = mat?.stocks || [];
        return (
          <Select
            size="small"
            value={rec.raw_material_stock_id}
            onChange={(stockId) => {
              const selectedStock = stocks.find((s) => s.id === stockId);
              if (selectedStock) {
                const cost =
                  (rec.quantity + (rec.wastage_quantity || 0)) *
                  selectedStock.cost_price;
                onUpdate(idx, {
                  raw_material_stock_id: stockId,
                  raw_material_batch_no: selectedStock.batch_no,
                  default_cost_price: selectedStock.cost_price,
                  cost: cost,
                });
              }
            }}
            className="w-40 text-xs"
            placeholder="Select batch"
            dropdownMatchSelectWidth={false}
          >
            {stocks.map((st) => (
              <Select.Option key={st.id} value={st.id}>
                {st.batch_no} (Rs. {st.cost_price.toFixed(2)} · Qty:{" "}
                {st.quantity})
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Unit Cost (Rs.)",
      key: "default_cost",
      render: (_: any, rec: RecipeIngredient) => (
        <span className="text-xs text-gray-500">
          Rs. {rec.default_cost_price?.toFixed(2) || "0.00"}
        </span>
      ),
    },
    {
      title: "Qty Required",
      key: "quantity",
      render: (_: any, rec: RecipeIngredient, idx: number) => (
        <Space>
          <InputNumber
            size="small"
            min={0.0001}
            value={rec.quantity}
            onChange={(v) => onUpdate(idx, { quantity: v || 0 })}
            className="w-20"
          />
          <span className="text-[11px] text-gray-400 capitalize">
            {rec.raw_material_unit}
          </span>
        </Space>
      ),
    },
    {
      title: "Wastage Qty",
      key: "wastage_quantity",
      render: (_: any, rec: RecipeIngredient, idx: number) => (
        <Space>
          <InputNumber
            size="small"
            min={0}
            value={rec.wastage_quantity}
            onChange={(v) => onUpdate(idx, { wastage_quantity: v || 0 })}
            className="w-20"
          />
          <span className="text-[11px] text-gray-400 capitalize">
            {rec.raw_material_unit}
          </span>
        </Space>
      ),
    },
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      render: (v: number) => (
        <span className="font-semibold text-gray-700 text-xs">
          Rs. {v?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "",
      key: "delete",
      width: 40,
      render: (_: any, __: any, idx: number) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<CloseOutlined />}
          onClick={() => onRemove(idx)}
        />
      ),
    },
  ];

  return (
    <Table
      dataSource={ingredients}
      columns={columns}
      pagination={false}
      size="small"
      rowKey={(rec, idx) => rec.raw_material_id || `${idx}`}
      className="rounded-md border border-gray-100 overflow-hidden"
      scroll={{ x: 700 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No ingredients added yet."
          />
        ),
      }}
    />
  );
};
